import { Col, Card, Table, Statistic, Button, Select, Input, InputNumber, Tag, Badge, Space, Tooltip } from 'antd';
import { EditOutlined, InfoCircleOutlined } from '@ant-design/icons';
import '../../styles/VersionManagement.scss';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { commaSeparatedString } from '../../utils/utils';
import type { ColumnsType } from 'antd/es/table';
import { Typography } from 'antd';
import { getTooltip } from '../services/tooltip_services';
import { useCurrency } from '../../context/CurrencyContext';


const { Text } = Typography;
const { TextArea } = Input;

const DealEconomicsSummariesDetails = ({ span, dataSource, grossNetDataSource, totalBillableHours, onChange, targetMarginDD, initialDealPricingComment, initialbelowTargetMarginReason, slice = 0, viewOnly = false }: { span: number; dataSource: any, grossNetDataSource: any, totalBillableHours: number, onChange: (data: any[]) => void, targetMarginDD: any[], initialDealPricingComment: string, initialbelowTargetMarginReason: string, slice?: number, viewOnly?: boolean }) => {
  const { dealCurrency, currencySymbol } = useCurrency();
  const [dealPricingComment, setDealPricingComment] = useState(initialDealPricingComment);
  const [belowTargetMarginReason, setBelowTargetMarginReason] = useState(initialbelowTargetMarginReason);


  const shouldBoldRow = (fieldName: string): boolean => {
    if (fieldName == `Project Revenue ${currencySymbol}` || fieldName == "Projected Client Margin %" || fieldName == "Variance to Target") {
      return true
    }
    return false
  }

  const columns: ColumnsType<any> = [
    {
      dataIndex: 'fieldName',
      key: 'fieldName',
      render: (value: string) => (
        <span style={{ fontWeight: `${shouldBoldRow(value) ? 'bold' : ''}` }}>{value}</span>
      )
    },
    {
      title: "Gross Project Total",
      dataIndex: 'gross',
      key: 'gross',
      align: 'center',
      render: (value: string, record: any) => (
        <span
          style={{
            color: ["Variance to Target"].includes(record.fieldName) ? parseFloat(value.replace('%', '')) < 0 ? 'red' : parseFloat(value.replace('%', '')) > 0 ? 'green' : undefined : undefined,
            fontWeight: `${shouldBoldRow(record['fieldName']) ? 'bold' : ''}`
          }}
        >
          {value !== '' ? value.toString().includes('%') ? value : `${currencySymbol} ${commaSeparatedString(value)}` : ''}
        </span>
      ),
    },
    {
      title: 'Contingency (+) & Discount (-)',
      dataIndex: 'discount',
      key: 'discount',
      align: 'center',
      render: (text: string, record: any) => <span style={{ fontWeight: `${shouldBoldRow(record['fieldName']) ? 'bold' : ''}` }}>
        {text ? text.toString().includes('%') ? text : `${currencySymbol} ${commaSeparatedString(text)}` : text}
      </span>,
    },
    {
      title: 'Net Project Total',
      dataIndex: 'net',
      key: 'net',
      align: 'center',
      render: (value: string, record: any) => (
        <span
          style={{
            color: ["Variance to Target"].includes(record.fieldName) ? parseFloat(value.replace('%', '')) < 0 ? 'red' : parseFloat(value.replace('%', '')) > 0 ? 'green' : undefined : undefined,
            fontWeight: `${shouldBoldRow(record['fieldName']) ? 'bold' : ''}`
          }}
        >
          {value !== '' ? value.toString().includes('%') ? value : `${currencySymbol} ${commaSeparatedString(value)}` : ''}
        </span>
      ),
    },
  ];

  useEffect(() => {
    setDealPricingComment(initialDealPricingComment);
    setBelowTargetMarginReason(initialbelowTargetMarginReason);
  }, [initialDealPricingComment, initialbelowTargetMarginReason])

  useEffect(() => {
    let dealEconomicsFinalData = [];
    if (dataSource.length === 0) {
      dealEconomicsFinalData = [{
        "blendHourlyPrice": '0',
        "totalProjectPrice": '0',
        "projectcontingencyAmount": '0',
        "projectDiscountAmount": '0',
        "netProjectPrice": '0',
        "totalProjectLaborCost": '0',
        "projectClientMargin": '0',
        "targetClientMargin": '0',
        "varianceToTargetMargin": '0',
        'totalBillableHours': '0',
        "belowTargetMarginReason": "",
        "dealPricingComment": dealPricingComment
      }]
    } else {
      dealEconomicsFinalData = dataSource.map((item: any) => ({
        "blendHourlyPrice": item['Blended Hourly Price'],
        "totalProjectPrice": item['Total Project Price'],
        "projectcontingencyAmount": item['Project Contingency Amount'],
        "projectDiscountAmount": item['Project Discount Amount'],
        "netProjectPrice": item['Net Project Price'],
        "totalProjectLaborCost": item['Total Project Labor Cost'],
        "projectClientMargin": item['Projected Client Margin %'],
        "targetClientMargin": item['Target Client Margin %'],
        "varianceToTargetMargin": item['Variance to Target Margin %'],
        'totalBillableHours': totalBillableHours,
        "belowTargetMarginReason": belowTargetMarginReason,
        "dealPricingComment": dealPricingComment
      }))
    }

    onChange(dealEconomicsFinalData);

  }, [dealPricingComment, belowTargetMarginReason, dataSource]);


  // Version Summaries
  const DealEconomicsSummaries: any = grossNetDataSource.reduce((acc: any, item: any, index: number) => {
    acc.push(item);
    if (index === 2 || index === 4) {
      // Insert a blank row after the 3rd and 6th rows
      acc.push({ key: index, fieldName: '', gross: '', discount: '', net: '' });
    }
    return acc;
  }, []);

  useEffect(() => {
    if (DealEconomicsSummaries.length > 0 && parseFloat(DealEconomicsSummaries[5]['net'].replace('%', '')) >= 0) {
      setBelowTargetMarginReason('');
    }
  }, [DealEconomicsSummaries]);

  const headerList = ['MVL TEAM STRUCTURE_SEPT092024', 'IND TEAM STRUCTURE_SEPT082024', 'US TEAM STRUCTURE_SEPT072024']

  const formatTableData = (summary: Record<string, string>) => {
    return Object.entries(summary).map(([field, value], index) => ({
      key: index,
      field,
      value
    }));
  };

  const navigate = useNavigate(); // React Router hook for navigation

  const handleNavigate = () => {
    navigate('/edit-pricing-details');  // Navigate to /edit-pricing page
  };

  // Display Versions
  const renderCards = () => {

    return (
      <Card
        className='version-card'
        title="Deal Economics Summary"
      >
        <h5> Project Scope</h5>

        <Space direction="horizontal" size="small" style={{ borderBottom: "1px solid #80808052", width: "50%", display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: "20px" }}>
          <Text style={{ fontSize: '16px' }}>Total Billable Hours:</Text>
          <Text style={{ fontSize: '16px' }}>{commaSeparatedString(totalBillableHours)}</Text>
        </Space>
        <Space direction="horizontal" size="small" style={{ borderBottom: "1px solid #80808052", width: "50%", display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: "40px" }}>
          <Text style={{ fontSize: '16px' }}>Total Billable Days:</Text>
          <Text style={{ fontSize: '16px' }}>{commaSeparatedString(Math.round(totalBillableHours / 8))}</Text>
        </Space>

        <h5> Deal Economics</h5>
        <Table
          rowKey="key"
          columns={columns}
          dataSource={DealEconomicsSummaries}
          pagination={false}
          size="small"
          rowClassName="small-table-row"
          onRow={(record, rowIndex) => ({
            style: {
              color: 'black',
            },
          })}
          style={{ marginBottom: '40px', width: '100%' }}
        />
        {DealEconomicsSummaries.length > 0 && parseFloat(DealEconomicsSummaries[5]['net'].replace('%', '')) < 0 &&
          <div>
            <Badge status="error" style={{ marginLeft: "5px" }} text={(
              <span>
                Reason for below Target Margin:
                <Tooltip title={<span dangerouslySetInnerHTML={{ __html: getTooltip("Reason for below Target Margin") || '' }} />}>
                  <InfoCircleOutlined style={{ marginLeft: "10px", color: "#4096FF" }} />
                </Tooltip>
              </span>
            )} />

            <Select style={{ width: '75%', marginLeft: '10px', marginBottom: '40px' }} disabled={viewOnly}
              onChange={(value) => setBelowTargetMarginReason(value)}
              status={parseFloat(DealEconomicsSummaries[5]['net'].replace('%', '')) < 0 && belowTargetMarginReason == "" ? "error" : ""}
              value={belowTargetMarginReason}>
              <option value="">Select a reason</option>
                {targetMarginDD.filter((item: any) => !item.reason.includes('Roundup')).map((item: any) => <option value={item.reason}>{item.reason}</option>)}
            </Select>
          </div>
        }
        <Badge status="default" style={{ marginLeft: "10px", marginRight: "5px" }} text="Deal Pricing Comment:" />
        <TextArea autoSize={{ minRows: 3, maxRows: 6 }} style={{ width: '100%', whiteSpace: 'pre-wrap' }} onChange={(e: any) => setDealPricingComment(e.target.value)} value={dealPricingComment} disabled={viewOnly} />
      </Card>
    );
  };

  return (
    <>{renderCards()}</>
  );
};

export default DealEconomicsSummariesDetails;
